<template>
    <div>
        <side-sheet
            ref="leadDetailSheet"
            :size="7"
            label="Lead detail pagina"
            description="Hier kun je de details inzien van de lead"
        >
            <template #default>
                <grid-container template="side-sheet" :color="Color.gray200">
                    <lead-detail-header-skeleton>
                        <lead-detail-header :lead="lead as Lead" />
                    </lead-detail-header-skeleton>

                    <grid-area area="side-sheet-content" style="overflow-y: hidden;">
                        <grid-container template="two-columns-sidebar">
                            <lead-detail-left :lead="lead as Lead" />

                            <lead-detail-right-skeleton>
                                <lead-detail-right :lead="lead as Lead" />
                            </lead-detail-right-skeleton>
                        </grid-container>
                    </grid-area>
                </grid-container>
                <router-view :key="route.name ?? 'router-view'" />
            </template>
        </side-sheet>
    </div>
</template>

<script setup lang="ts">
    import { Color, type ExposedOverlay, GridArea, GridContainer, SideSheet } from '@keyvue/one-ui';

    import LeadDetailHeader from '@views/Lead/Detail/Areas/LeadDetailHeader.vue';
    import LeadDetailLeft from '@views/Lead/Detail/Areas/LeadDetailLeft.vue';
    import LeadDetailRight from '@views/Lead/Detail/Areas/LeadDetailRight.vue';
    import LeadDetailHeaderSkeleton from '@components/Skeleton/Area/LeadDetailHeaderSkeleton.vue';
    import LeadDetailRightSkeleton from '@components/Skeleton/Area/LeadDetailRightSkeleton.vue';

    import {
        Lead,
        LeadId,
        RegisterLeadViewedCommand,
    } from '@keyvue/one-api-client';

    import { onMounted, ref } from 'vue';
    import useClient from '@/composables/useClient';
    import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
    import useOverlayAsRoute from '@composables/useOverlayAsRoute';
    import useParentRoute from '@composables/useParentRoute';
    import useEventBus from '@composables/useEventBus';

    interface Props {
        leadId: string;
    }

    const props = defineProps<Props>();
    const { client } = useClient();

    const eventBus = useEventBus();
    const route = useRoute();
    const router = useRouter();
    const createRouteLocation = useParentRoute();

    const leadDetailSheet = ref<ExposedOverlay>();

    const lead = ref<Lead>(await client.get(Lead, props.leadId));

    useOverlayAsRoute({
        overlay: leadDetailSheet,
        isSideSheet: true,
        redirectMethod: () => {
            router.push(createRouteLocation({
                name: 'contactDetail',
            }));
        },
    });

    const registerLeadViewed = (currentRouteName?: string) => {
        if (!currentRouteName?.endsWith('LeadDetail')) {
            return;
        }

        client.dispatch(
            new RegisterLeadViewedCommand(new LeadId(props.leadId)),
        );
    };

    onMounted(async () => {
        registerLeadViewed(route.name?.toString());
    });

    onBeforeRouteUpdate(async (to) => {
        registerLeadViewed(to.name?.toString());
    });

    eventBus.subscribe<string>('resource-manipulated', async (params) => {
        if (params === 'Lead') {
            lead.value = await client.get(Lead, props.leadId);
        }
    });

</script>
